import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { IDownloadFilesDialogComponentData, IDownloadItem } from './interfaces';
import { FileDownloadService } from '../file-download.service';
import { TRANSLATION_MAP } from '../const';
import { SharedService } from '../shared.service';
import { NotificationMessageType, NotificationsService } from '@azavista/components/shared';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { AzavistaButtonModule } from '@azavista/components/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getFormattedFileNameWithExtension } from '../file.mime.utils';

/**
 * - `FileSystemDirectoryHandle`
 * - `false` if the user does not give permission to access the directory
 * - `undefined` if the browser does not support directory picking
 */
export type FileSystemDirectoryHandleResponse = FileSystemDirectoryHandle | false | undefined;

@Component({
    templateUrl: './download-files-dialog.component.html',
    styleUrls: ['./download-files-dialog.component.css'],
    imports: [
        CommonModule,
        TranslateModule,

        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatDialogModule,

        AzavistaButtonModule,
    ],
    standalone: true
})
export class DownloadFilesDialogComponent {
    maxItemsInQueue = 5;
    fileDownloadPercentageMap: { [itemUrl: string]: number } = {};
    fileDownloadError: { [itemUrl: string]: string } = {};
    isDownloadingAll = false;
    isDownloadedAll = false;
    TRANSLATION_MAP = TRANSLATION_MAP;

    constructor(
        public dialogRef: MatDialogRef<DownloadFilesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IDownloadFilesDialogComponentData,
        private notificationSvc: NotificationsService,
        private sharedSvc: SharedService,
        private downloadSvc: FileDownloadService
    ) {
        data.items = data.items.map(item => ({
            ...item,
            label: item.label === item.name ? getFormattedFileNameWithExtension(item) : item.label,
            name: getFormattedFileNameWithExtension(item)
        }));
    }

    async downloadAllFiles(useFolder: boolean): Promise<void> {
        this.isDownloadingAll = true;
        const queuedItems = this.data.items.concat();
        const directoryHandle: FileSystemDirectoryHandleResponse = useFolder ? await this.getDirectoryHandle() : undefined;

        if (directoryHandle === false) {
            return;
        }

        queuedItems.forEach(item => this.fileDownloadPercentageMap[item.url] = 0);

        const startQueueItem = async (itemCount: number) => {
            const items = queuedItems.splice(0, itemCount);
            const downloadPromises: Promise<void>[] = [];
            for (const item of items) {
                if (!this.isDownloadInProgress(item)) {
                    this.fileDownloadPercentageMap[item.url] = 0;
                    downloadPromises.push((async () => {
                        await this.handleDownloadFile(item, directoryHandle || undefined);
                        if (queuedItems.length > 0) {
                            await startQueueItem(1);
                        }
                    })());
                }
            }
            await Promise.all(downloadPromises);
        };

        await startQueueItem(this.maxItemsInQueue);

        this.isDownloadingAll = false;
        this.isDownloadedAll = true;
    }

    async handleDownloadFile(item: IDownloadItem, directoryHandle: FileSystemDirectoryHandle | undefined): Promise<void> {
        if (this.isAddedToDownload(item) && this.isDownloadInProgress(item)) {
            return;
        }
        try {
            delete this.fileDownloadError[item.url];
            this.fileDownloadPercentageMap[item.url] = 0;
            if (directoryHandle) {
                await this.downloadSvc.saveFileToDirectory(item, directoryHandle, (progress) => {
                    this.fileDownloadPercentageMap[item.url] = progress.percentage;
                });
            } else {
                await this.downloadSvc.saveFile(item.url, item.name);
            }
            this.fileDownloadPercentageMap[item.url] = 100;
        } catch (error: any) {
            const errorMessage = this.sharedSvc.getErrorText(error);
            this.fileDownloadError[item.url] = errorMessage;
            this.notificationSvc.showMessage(NotificationMessageType.error, `${item.name}: ${errorMessage}`);
        }
    }

    isDownloadInProgress(item: IDownloadItem) {
        return this.isDownloadStarted(item) && this.fileDownloadPercentageMap[item.url] < 100;
    }

    isDownloadCompleted(item: IDownloadItem) {
        return this.fileDownloadPercentageMap[item.url] === 100;
    }

    isDownloadInQueue(item: IDownloadItem) {
        return this.fileDownloadPercentageMap[item.url] === 0;
    }

    isAddedToDownload(item: IDownloadItem) {
        return this.fileDownloadPercentageMap[item.url] != null;
    }

    isDownloadStarted(item: IDownloadItem) {
        return this.fileDownloadPercentageMap[item.url] > 0;
    }

    onCloseClick(): void {
        this.dialogRef.close();
    }

    async getDirectoryHandle(): Promise<FileSystemDirectoryHandleResponse>{
        if ('showDirectoryPicker' in window) {
            return (await (window as any).showDirectoryPicker({
                mode: 'readwrite'
            })) || false;
        } else {
            this.notificationSvc.showMessage(NotificationMessageType.warning, this.sharedSvc.translate(TRANSLATION_MAP.PLEASE_USE_CHROME_TO_DOWNLOAD_TO_FOLDER));
            return undefined;
        }
    }
}
